
// src/pages/AboutUsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

// Import your images
import glows from '../assets/images/glows.gif';
import integrityImg from '../assets/images/integrity.png';
import innovationImg from '../assets/images/innovation.png';
import collaborationImg from '../assets/images/collaboration.png';
import centricityImg from '../assets/images/centricity.jpg';

// Import components
import FeaturesSection from '../components/FeaturesSection';
import WhiteContactForm from '../components/WhiteContactForm';
import AnimatedSection, {
    fadeIn,
    fadeInUp,
    fadeInLeft,
    fadeInRight,
    scaleUp,
    staggerItemFadeInUp
} from '../components/AnimatedSection';

// --- FIXED SVG IMPORTS ---
// This syntax works best with Create React App. For Vite, you might need a plugin like vite-plugin-svgr.
import { ReactComponent as AdvisorsIcon } from '../assets/images/advisors.svg';
import { ReactComponent as MoroccanIcon } from '../assets/images/moroccan.svg';
import { ReactComponent as ProductIcon } from '../assets/images/product.svg';

// Define the data for the slider
const valuesData = [
    { title: 'Integrity', text: 'We conduct our business with the highest ethical standards, maintaining transparency and accountability in all our interactions.', image: integrityImg },
    { title: 'Innovation', text: 'We embrace a culture of continuous learning and exploration, pushing blockchain boundaries to create groundbreaking solutions.', image: innovationImg },
    { title: 'Collaboration', text: 'We believe in the power of partnership and work closely with our clients and stakeholders to achieve shared success.', image: collaborationImg },
    { title: 'Customer Centricity', text: 'We prioritize understanding and exceeding our client\'s needs, delivering exceptional service and support throughout their blockchain journey.', image: centricityImg },
];

const teamData = [
    { icon: AdvisorsIcon, title: "Advisors", description: "60+ years combined expertise in banking, operations, Web3, and business strategy" },
    { icon: MoroccanIcon, title: "Moroccan Engineers", description: "40+ yrs. combined expertise in business process management and blockchain technology" },
    { icon: ProductIcon, title: "Product Designers", description: "30+ yrs. combined expertise in user experience design and user interface strategy" },
];

const AboutUsPage = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const intervalRef = useRef(null);
    const slideInterval = 5000;

    const stopAutoSlide = () => {
        clearInterval(intervalRef.current);
    };

    const nextSlide = () => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % valuesData.length);
    };

    const startAutoSlide = () => {
        stopAutoSlide();
        intervalRef.current = setInterval(nextSlide, slideInterval);
    };

    const showSlide = (index) => {
        setCurrentIndex(index);
        startAutoSlide(); // Restart timer on manual click
    };

    useEffect(() => {
        startAutoSlide();
        return () => stopAutoSlide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const missionIcon = (
        <svg className="h-10 w-10 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1" fill="none" />
            <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1" fill="none" />
            <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1" fill="currentColor" />
        </svg>
    );

    const visionIcon = (
        <svg className="h-10 w-10 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    );

    return (
        <>
            {/* Reimagining Infrastructure Section */}
            <div className="py-16 lg:py-24 bg-black text-white">
                <div className="container mx-auto px-6 lg:px-12 w-full lg:w-10/12 mt-12">
                    {/* Layout is a single column for mobile/tablet, and a row for large screens (desktops) */}
                    <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                        
                        {/* Text Content */}
                        <AnimatedSection 
                            triggerOnce={false} 
                            tag="div" 
                            animationVariants={fadeInLeft} 
                            className="w-full lg:w-6/12 space-y-6 text-left"
                        >
                            {/* 1. Title (appears first on all screen sizes) */}
                            <h1 className="text-4xl lg:text-5xl font-semibold leading-tight mt-6 lg:mt-0">
                                Reimagining Infrastructure for the Decentralized Era
                            </h1>
                            {/* 2. Paragraph (appears second on all screen sizes) */}
                            <p className="text-lg text-gray-400 leading-relaxed">
                                At ZenithBlox, we are at the forefront of merging traditional finance with the innovative potential of decentralized technologies. Our mission is to empower financial institutions to adapt and thrive—unlocking new revenue opportunities, enhancing operational efficiency, and fostering trust through secure and scalable blockchain solutions. With a strong emphasis on user experience and tangible impact, we guide organizations confidently into the transformative Web3 landscape.
                            </p>
                        </AnimatedSection>

                        {/* Image Content */}
                        <AnimatedSection 
                            triggerOnce={false} 
                            tag="div" 
                            animationVariants={fadeInRight} 
                            className="w-full lg:w-6/12 flex justify-center"
                        >
                            <div className="relative p-4 max-w-lg w-full">
                                <motion.img
                                    variants={scaleUp}
                                    className="w-full h-auto rounded-lg shadow-2xl"
                                    src={glows}
                                    alt="ZenithBlox Infrastructure"
                                    initial="hidden"
                                    animate="visible"
                                />
                            </div>
                        </AnimatedSection>

                    </div>
                </div>
            </div>

            {/* Features Section */}
            <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className='bg-white'>
                <FeaturesSection />
            </AnimatedSection>

            {/* Mission Vision Section */}
            <div className="bg-black text-white py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="text-left mb-6">
                        <span className='w-fit inline-block border border-[#817AF7] text-[#817AF7] rounded-full px-4 py-1 text-sm font-medium mb-6'>
                            Mission & Vision
                        </span>
                    </AnimatedSection>
                    <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="text-left max-w-3xl mb-12 lg:mb-16">
                        <h2 className="text-4xl sm:text-5xl font-bold mb-4">Why We Exist & Where We're Going</h2>
                        <p className="text-lg text-gray-400">Rooted in a clear purpose, our mission drives everything we do today—while our vision lights the way toward the future we’re working to create.</p>
                    </AnimatedSection>
                    <AnimatedSection
                        triggerOnce={false}
                        animationVariants={fadeIn}
                        staggerChildren={0.2}
                        className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
                    >
                        <motion.div variants={staggerItemFadeInUp} className="bg-[#1E1E1E] p-8 rounded-2xl shadow-xl border border-transparent hover:border-purple-500/50 transition-colors duration-300">
                            <div className="mb-6">{missionIcon}</div>
                            <h3 className="text-2xl font-semibold mb-4">Our Mission</h3>
                            <p className="text-gray-400 leading-relaxed">At ZenithBlox, our mission is to empower financial institutions worldwide with seamless blockchain integration. We strive to provide cutting-edge solutions that drive innovation, increase operational efficiency, and foster trust within the global financial ecosystem.</p>
                        </motion.div>
                        <motion.div variants={staggerItemFadeInUp} className="bg-[#1E1E1E] p-8 rounded-2xl shadow-xl border border-transparent hover:border-purple-500/50 transition-colors duration-300">
                            <div className="mb-6">{visionIcon}</div>
                            <h3 className="text-2xl font-semibold mb-4">Our Vision</h3>
                            <p className="text-gray-400 leading-relaxed">We envision a future where financial transactions are secure, transparent, and accessible to all. ZenithBlox is dedicated to being the leading provider of blockchain integration solutions, shaping the future of finance and enabling a more inclusive and efficient global economy.</p>
                        </motion.div>
                    </AnimatedSection>
                </div>
            </div>

            {/* Values Slider Section */}
            <div className="bg-white text-black py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="mb-6">
                        <span className='w-fit inline-block border border-[#817AF7] text-[#817AF7] rounded-full px-4 py-1 text-sm font-medium mb-6'>Values</span>
                    </AnimatedSection>
                    <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="max-w-3xl mb-12 lg:mb-16">
                        <h2 className="text-4xl sm:text-5xl font-bold mb-4">Built on Values</h2>
                        <p className="text-lg text-gray-600">The principles that guide our actions, shape our culture, and define how we work together and serve our mission.</p>
                    </AnimatedSection>
                    <AnimatedSection
                        triggerOnce={false}
                        animationVariants={fadeIn}
                        className="bg-black text-white p-8 sm:p-12 rounded-3xl shadow-2xl overflow-hidden"
                        onMouseEnter={stopAutoSlide}
                        onMouseLeave={startAutoSlide}
                    >
                        <div className="relative min-h-[500px] sm:min-h-[450px] md:min-h-[400px] lg:min-h-[380px]">
                            {valuesData.map((value, index) => (
                                <div key={value.title} className={`slide-item absolute top-0 left-0 w-full transition-opacity duration-1000 ease-in-out ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                                        <div className="text-left">
                                            <h3 className="text-3xl sm:text-4xl font-semibold mb-6">{value.title}</h3>
                                            <p className="text-lg text-gray-400 leading-relaxed">{value.text}</p>
                                        </div>
                                        <div className="relative order-first md:order-last">
                                            <div className="bg-white w-full h-64 sm:h-80 rounded-2xl flex items-center justify-center text-gray-400 mb-12 md:mb-16">
                                                <img src={value.image} alt={value.title} className="object-cover w-full h-full rounded-2xl" />
                                            </div>
                                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-gray-800/70 backdrop-blur-sm px-4 py-2 rounded-full flex space-x-2">
                                                {valuesData.map((_, dotIndex) => (
                                                    <span key={dotIndex} onClick={() => showSlide(dotIndex)} className={`block w-2.5 h-2.5 rounded-full cursor-pointer transition-colors ${dotIndex === currentIndex ? 'bg-white' : 'bg-gray-500 hover:bg-gray-400'}`}></span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </AnimatedSection>
                </div>
            </div>

            {/* Our Team Section */}
            <div className="bg-black text-white py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="text-left mb-6">
                        <span className='w-fit inline-block border border-[#817AF7] text-[#817AF7] rounded-full px-4 py-1 text-sm font-medium mb-6'>Our Team</span>
                    </AnimatedSection>
                    <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="text-left max-w-3xl mb-12 lg:mb-16">
                        <h2 className="text-4xl sm:text-5xl font-bold mb-4">Expertise That Sets Us Apart</h2>
                        <p className="text-lg text-gray-400">A deeply experienced team driven by curiosity, collaboration, and a commitment to excellence in every detail.</p>
                    </AnimatedSection>
                    <AnimatedSection
                        triggerOnce={false}
                        animationVariants={fadeIn}
                        staggerChildren={0.2}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {teamData.map((member, index) => {
                            const IconComponent = member.icon;
                            return (
                                <motion.div
                                    key={index}
                                    variants={staggerItemFadeInUp}
                                    className="bg-[#1E1E1E] p-8 rounded-2xl shadow-xl border border-transparent hover:border-purple-500/50 transition-colors duration-300 flex flex-col"
                                >
                                    <div className="mb-6">{IconComponent && <IconComponent className="h-10 w-10 text-purple-500" />}</div>
                                    <h3 className="text-xl sm:text-2xl font-semibold mb-4">{member.title}</h3>
                                    <p className="text-gray-400 leading-relaxed flex-grow">{member.description}</p>
                                </motion.div>
                            );
                        })}
                    </AnimatedSection>
                </div>
            </div>

            {/* WhiteContactForm - Animates as a whole */}
            <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} threshold={0.05}>
                <WhiteContactForm/>
            </AnimatedSection>
        </>
    );
};

export default AboutUsPage;